// Displays a drag-and-drop attachment panel that can be used by fields for uploading to S3
// Emits events whenever a new file is uploaded

<template>
  <div>
    <loading-spinner v-if="isLoadingSpinner"></loading-spinner>
    <div class="fr-popup fr-desktop fr-active file-upload">
      <div
        class="fr-file-upload-layer fr-layer fr-active"
        style="width: 100%;"
        :class="{'drag-over': draggingFileOver}"
        @dragover="draggingFileOver=true"
        @dragleave="draggingFileOver=false"
      >
        <strong>Drop file</strong>
        <br>(or click)
        <div class="fr-form">
          <input
            @change="onFileInserted($event)"
            type="file"
            name="file"
            :accept="validFileTypes"
            tabindex="-1"
            aria-labelledby="fr-file-upload-layer-6"
            role="button"
            dir="auto"
            ref="fileUploader"
            @click="resetFileUploader"
            :multiple="multipleSelectionAllowed"
            @dragover.prevent
            @drop.prevent="onDrop"
          >
        </div>
      </div>
    </div>
    <div class="text-danger input-error-text" v-if="errorMessage">{{errorMessage}}</div>
    <span class="text-danger input-error-text" v-if="errorMessageForEncryptedFiles">The following file(s) are password protected: {{encryptedFiles}}. Please attach files which are not password protected.<br></span>
    <span class="text-danger input-error-text" v-if="errorMessageInCaseOfUnsupportedFile">The following file(s) are of unsupported format: {{unSupportedFiles}}. Please refer this<a :href="CONSTANTS.INVOICE_UPLOAD_FILE_CONVERSION" target="_blank"> wiki</a> for converting the file(s) to supported format.</span>
  </div>
</template>

<script>
import api from "@/api";
import LoadingSpinner from "@/components/LoadingSpinner";
import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/js/plugins.pkgd.min.js";
import CONSTANTS from "@/store/constants";

export default {
  name: "invoice-upload",
  props: ["existingFiles", "maxFileSizeInBytes","validFileTypes","multipleSelectionAllowed","checkForFileSupport"],
  components: { LoadingSpinner },
  data() {
    return {
      draggingFileOver: false,
      errorMessage: null,
      encryptedFiles: '',
      unSupportedFiles: '',
      errorMessageForEncryptedFiles: false,
      errorMessageInCaseOfUnsupportedFile: false,
      isLoadingSpinner: false,
    };
  },
  methods: {
    onDrop(event) {
      this.errorMessage = null;
      this.encryptedFiles = ''
      this.unSupportedFiles = ''
      this.errorMessageInCaseOfUnsupportedFile = false
      this.errorMessageForEncryptedFiles = false
      this.draggingFileOver = false;
      this.uploadFiles(event.dataTransfer.files);
    },
    resetFileUploader() {
      this.$refs.fileUploader.value = "";
    },
    onFileInserted(event) {
      this.errorMessage = null;
      this.encryptedFiles = ''
      this.unSupportedFiles = ''
      this.errorMessageInCaseOfUnsupportedFile = false
      this.errorMessageForEncryptedFiles = false
      this.uploadFiles(event.currentTarget.files);
    },
    clearErrors() {
      setTimeout(() => { this.errorMessage = ""}, 4000)
    },
    isFileSizeInvalid(currentFileSize) {
      let existingFilesSize = 0
      if(this.existingFiles && this.existingFiles.length > 0) {
        this.existingFiles.forEach(file => {
          existingFilesSize += parseFloat(file.fileSize)
        })

        if(parseFloat(currentFileSize) > parseFloat(this.maxFileSizeInBytes)) return true
      }
      if(parseFloat(existingFilesSize) + parseFloat(currentFileSize) > parseFloat(this.maxFileSizeInBytes)) return true
      return false
    },
    isFileSizeZero(fileList){
      return fileList.some(fileItem => parseFloat(fileItem.size) === 0 )
    },
    uploadFiles(files) {
      if (!files) return;

      // Calculating total size of all the files selected.
      let fileList = [];
      if (this.multipleSelectionAllowed) {
         fileList = [...files]
      }else{
        fileList.push(files[0])
      }

      let totalFileSize =  parseFloat(fileList.reduce((sum, fileItem) => sum + fileItem.size, 0));
      let newFileNames = fileList.map((file) => file.name)

      if(this.isFileSizeInvalid(totalFileSize)) {
        this.errorMessage = CONSTANTS.ATTACHMENT_VALIDATION_ERROR_MESSAGE.FILE_SIZE_EXCEEDED_ERROR
        this.clearErrors()
        return
      }
      if (this.isFileSizeZero(fileList)){
        this.errorMessage = CONSTANTS.ATTACHMENT_VALIDATION_ERROR_MESSAGE.ZERO_KB_ERROR
        this.clearErrors()
        return
      }
      if(!this.fileExtensionsAreValid(fileList)) {
        this.errorMessage = CONSTANTS.ATTACHMENT_VALIDATION_ERROR_MESSAGE.INVALID_FILE_EXTENSION_ERROR + this.validFileTypes
        this.clearErrors()
        return
      }
      if (this.fileNameExist(newFileNames)) {
        this.errorMessage = CONSTANTS.ATTACHMENT_VALIDATION_ERROR_MESSAGE.ALREADY_EXIST_ERROR
        this.clearErrors()
        return
      }
      if (this.isFileNameExceedingAllowedLength(newFileNames)){
        this.errorMessage = CONSTANTS.ATTACHMENT_VALIDATION_ERROR_MESSAGE.FILE_NAME_50_CHAR_ERROR
        this.clearErrors()
        return
      }

      if (files.length == 0) return;
      var UUID = this.$uuid.v4();

      fileList.forEach((file) => {
        if (this.isValidFileName(file.name)) {
          this.isLoadingSpinner = true;
          var uploadedFileId = UUID + "_" + file.name;
          api.getPresignedUrl(uploadedFileId,false).then(response => {
            var preSignedUrl = response.data.data.fetchAttachmentPreSignedUrl.preSignedUrl;
            api
                .uploadApp(file, preSignedUrl)
                .then(() => {
                  if(this.checkForFileSupport) {
                    api.checkFileUploadStatus(uploadedFileId).then((statusResponse) => {
                      if(statusResponse && statusResponse.data && statusResponse.data.data && statusResponse.data.data.fetchAttachmentUploadStatusById.encryptedFile){
                        this.updateEncryptedFiles(file)
                      }else if (statusResponse && statusResponse.data && statusResponse.data.data && statusResponse.data.data.fetchAttachmentUploadStatusById.unSupportedFile){
                        this.updateUnsupportedFiles(file)
                      }else{
                        this.errorMessageForEncryptedFiles = false
                        this.errorMessageInCaseOfUnsupportedFile = false
                        this.encryptedFiles = ''
                        this.unSupportedFiles = ''
                        this.isLoadingSpinner = false;
                        this.$emit("onFileUploaded", uploadedFileId, file.name, file.size);
                      }
                    })
                  } else {
                    this.isLoadingSpinner = false;
                    this.$emit("onFileUploaded", uploadedFileId, file.name, file.size);
                  }
                })
                .catch(error => {
                    this.errorMessage = error;
                }).finally(() => {
                    this.isLoadingSpinner = false
            });
          });
        } else {
          this.isLoadingSpinner = false;
        }
      })
    },
    updateUnsupportedFiles(file){
      this.errorMessageInCaseOfUnsupportedFile = true
      this.unSupportedFiles = this.unSupportedFiles.length === 0 ? this.unSupportedFiles = file.name : this.unSupportedFiles += ', ' + file.name
    },
    updateEncryptedFiles(file){
      this.errorMessageForEncryptedFiles = true
      this.encryptedFiles = this.encryptedFiles.length === 0 ? this.encryptedFiles = file.name : this.encryptedFiles += ', ' + file.name
    },
    fileExtensionsAreValid(fileList) {
      let extensions = fileList.map((file) => file.name.split('.').pop());
      return extensions.every((ext) => this.validFileTypes.includes(ext.toLowerCase()))
    },
    fileNameExist(newFileNames) {
      let existingFileNames = []
      this.existingFiles.forEach(file => {
        existingFileNames.push(file.fileName)
      })
      if(existingFileNames.some((fileName) => newFileNames.includes(fileName))) return true
      return false
    },
    isFileNameExceedingAllowedLength(files){
      return files.some(file => file.length > 50)
    },
    isValidFileName(fileName) {
      var hasBadSymbol = false;
      var fileNameBadSymbols = "";
      this.CONSTANTS.FILE_NAMING_BAD_SYMBOL_LIST.forEach(symbol => {
        if (fileName.includes(symbol)) {
          hasBadSymbol = true;
          fileNameBadSymbols += symbol;
        }
      });
      if (hasBadSymbol) {
        this.errorMessage = this.CONSTANTS.ERROR_BAD_FILENAME_SYMBOL.concat(
          fileNameBadSymbols,
          this.CONSTANTS.FILE_NAMING_ALLOWED_SYMBOLS_MESSAGE
        );

        return false;
      }
      return true;
    }
  }
};
</script>

<style scoped>
.file-upload {
  border-top: 0;
  box-shadow: none;
  -webkit-box-shadow: none;
}
.fr-popup {
  position: relative;
  font-family: artifaktElementRegular;
  z-index: 0 !important;
  margin: 0;
}
.fr-popup .fr-layer {
  margin: 0;
  margin-bottom: 1em;
}
.fr-popup .fr-file-upload-layer.drag-over {
  background: #ebebeb !important;
}
.loading-overlay {
  opacity: 0.5;
  background-color: #f5f5f5;
  z-index: 10;
}
</style>